import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createProject, updateProject, getProjectById } from '../services/projectService';
import { roundUp2 } from '../lib/utils';
import { toast } from 'sonner';
import { PageLoading } from './ui/Loading';

// Import new components
import ProjectFormHeader from './ProjectForm/ProjectFormHeader';
import ProjectFormSidebar from './ProjectForm/ProjectFormSidebar';
import StepNavigation from './ProjectForm/StepNavigation';
import BasicInfoStep from './ProjectForm/BasicInfoStep';
import PricingStep from './ProjectForm/PricingStep';
import PaymentStep from './ProjectForm/PaymentStep';
import ExpensesStep from './ProjectForm/ExpensesStep';
import OtherExpensesStep from './ProjectForm/OtherExpensesStep';
import DevelopersStep from './ProjectForm/DevelopersStep';
import SummaryStep from './ProjectForm/SummaryStep';

const ProjectForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  
  // Modern UI State
  const [currentStep, setCurrentStep] = useState(0);
  
  // Step configuration
  const steps = [
    { 
      id: 'basic', 
      title: 'ข้อมูลพื้นฐาน', 
      description: 'กรอกข้อมูลโปรเจคและลูกค้า'
    },
    { 
      id: 'pricing', 
      title: 'ราคาและการคำนวณ', 
      description: 'กำหนดราคาและระบบการคำนวณ'
    },
    { 
      id: 'payment', 
      title: 'การชำระเงิน', 
      description: 'จัดการงวดชำระและการรับเงิน'
    },
    { 
      id: 'expenses', 
      title: 'ค่าใช้จ่าย', 
      description: 'จัดการค่าใช้จ่ายบริษัทและโปรเจค'
    },
    { 
      id: 'other_expenses', 
      title: 'ค่าใช้จ่ายอื่นๆ', 
      description: 'จัดการค่าใช้จ่ายเพิ่มเติมของโปรเจค'
    },
    { 
      id: 'developers', 
      title: 'Developer', 
      description: 'จัดการค่าจ้างและรายละเอียด Developer'
    },
    { 
      id: 'summary', 
      title: 'สรุปและบันทึก', 
      description: 'ตรวจสอบข้อมูลและบันทึกโปรเจค'
    }
  ];

  // Form state
  const [formData, setFormData] = useState({
        project_name: '',
        client_name: '',
        project_price: 0,
        price_type: 'exclude_vat',
        discount: { enabled: false, type: 'percentage', percentage: 0, amount: 0 },
        vat: { enabled: true, percentage: 7, amount: 0 },
        withholding_tax: { enabled: true, percentage: 3, amount: 0 },
        final_amount: 0,
        payment_schedule: [],
        company_fee: { 
          percentage: 0, 
          amount: 0, 
          type: 'percentage',
          distribution_type: 'percentage',
          installment_distribution: []
        },
        sales_commission: {
          percentage: 0,
          amount: 0,
          type: 'percentage',
          distribution_type: 'percentage',
          installment_distribution: []
        },
        developer_payments: [],
    other_expenses: [],
        sales_name: '',
        sales_contact: ''
      });

  const [loading, setLoading] = useState(false);

  // Navigation functions
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Calculate amounts function
  const calculateAmounts = useCallback(() => {
    const basePrice = formData.project_price || 0;
    
    // Parse JSON fields safely
    const discount = (() => {
      try {
        return typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
      } catch (e) {
        return { enabled: false, type: 'percentage', percentage: 0, amount: 0 };
      }
    })();
    
    const vat = (() => {
      try {
        return typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
      } catch (e) {
        return { enabled: true, percentage: 7, amount: 0 };
      }
    })();
    
    const withholdingTax = (() => {
      try {
        return typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
      } catch (e) {
        return { enabled: true, percentage: 3, amount: 0 };
      }
    })();
    
    const discountAmount = discount.enabled 
      ? (discount.type === 'percentage' 
          ? roundUp2((basePrice * (discount.percentage || 0)) / 100)
          : roundUp2(discount.amount || 0))
      : 0;
    const priceAfterDiscount = roundUp2(basePrice - discountAmount);
    
    let priceBeforeVat, vatAmount, finalAmount;
    
    if (formData.price_type === 'include_vat') {
      // ราคารวม VAT แล้ว - แยก VAT ออก
      if (vat.enabled) {
        priceBeforeVat = roundUp2(priceAfterDiscount / (1 + (vat.percentage || 0) / 100));
        vatAmount = roundUp2(priceAfterDiscount - priceBeforeVat);
        finalAmount = priceAfterDiscount; // ราคารวม VAT แล้ว
      } else {
        priceBeforeVat = priceAfterDiscount;
        vatAmount = 0;
        finalAmount = priceAfterDiscount;
      }
    } else {
      // ราคาไม่รวม VAT - บวก VAT เข้าไป
      priceBeforeVat = priceAfterDiscount;
      vatAmount = vat.enabled 
        ? roundUp2((priceAfterDiscount * (vat.percentage || 0)) / 100)
        : 0;
      finalAmount = roundUp2(priceAfterDiscount + vatAmount);
    }
    
    const withholdingTaxAmount = withholdingTax.enabled
      ? (withholdingTax.type === 'amount' 
          ? (withholdingTax.amount || 0)
          : roundUp2((priceBeforeVat * (withholdingTax.percentage || 0)) / 100))
      : 0;
    
    // ยอดรับโอน = มูลค่ารวม - WHT
    const actualReceived = roundUp2(finalAmount - withholdingTaxAmount);
    
    // Parse company_fee safely
    const companyFee = (() => {
      try {
        return typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
      } catch (e) {
        return { type: 'percentage', percentage: 0, amount: 0 };
      }
    })();
    
    // Parse sales_commission safely
    const salesCommission = (() => {
      try {
        return typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
      } catch (e) {
        return { type: 'percentage', percentage: 0, amount: 0 };
      }
    })();
    
    const totalCompanyFeeAmount = companyFee.type === 'percentage'
      ? roundUp2((finalAmount * (companyFee.percentage || 0)) / 100)
      : roundUp2(companyFee.amount || 0);
    
    const totalSalesCommissionAmount = salesCommission.type === 'percentage'
      ? roundUp2((finalAmount * (salesCommission.percentage || 0)) / 100)
      : roundUp2(salesCommission.amount || 0);
    
    const totalOtherExpensesAmount = formData.other_expenses.reduce(
      (sum, expense) => sum + (expense.amount || 0), 0
    );
    
    const remainingBeforeDev = roundUp2(
      finalAmount - totalCompanyFeeAmount - totalSalesCommissionAmount - totalOtherExpensesAmount
    );
    
    const totalDevPaymentsAmount = formData.developer_payments.reduce(
      (sum, dev) => sum + (dev.amount || 0), 0
    );

        return {
      projectPrice: basePrice,
      discountAmount,
      discountPercentage: discount.percentage || 0,
      priceAfterDiscount,
      priceBeforeVat,
      vatAmount,
      vatPercentage: vat.percentage || 0,
      finalAmount,
      withholdingTaxAmount,
      withholdingTaxPercentage: withholdingTax.percentage || 0,
      actualReceived,
      totalCompanyFeeAmount,
      totalSalesCommissionAmount,
      totalOtherExpensesAmount,
      remainingBeforeDev,
      totalDevPaymentsAmount
    };
  }, [formData]);

  // Load project data for editing
  useEffect(() => {
    if (isEdit && id) {
      const loadProject = async () => {
        try {
          setLoading(true);
          const result = await getProjectById(id);
          if (result && result.data) {
            console.log('Loaded project data for edit:', result.data);
            setFormData(result.data);
      } else {
            toast.error('ไม่พบข้อมูลโปรเจค');
          }
        } catch (err) {
          console.error('Error loading project:', err);
          toast.error('ไม่สามารถโหลดข้อมูลโปรเจคได้');
        } finally {
          setLoading(false);
        }
      };
      loadProject();
    }
  }, [isEdit, id]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_name.trim() || !formData.client_name.trim()) {
      toast.error('กรุณากรอกชื่อโปรเจคและชื่อลูกค้า');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Starting project save...', { isEdit, id, formData });

      const projectData = {
        ...formData,
        ...calculateAmounts()
      };

      console.log('Project data to save:', projectData);

      let result;
      if (isEdit) {
        console.log('Updating project...');
        result = await updateProject(id, projectData);
        console.log('Update result:', result);
        if (result.error) {
          throw new Error(result.error.message || 'Failed to update project');
        }
        toast.success('อัปเดตโปรเจคเรียบร้อยแล้ว');
      } else {
        console.log('Creating project...');
        result = await createProject(projectData);
        console.log('Create result:', result);
        if (result.error) {
          throw new Error(result.error.message || 'Failed to create project');
        }
        toast.success('สร้างโปรเจคเรียบร้อยแล้ว');
      }
      
      console.log('Project saved successfully, redirecting to projects page...');
      navigate('/projects');
    } catch (err) {
      console.error('Error saving project:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        formData: formData,
        isEdit: isEdit,
        id: id
      });
      toast.error(`เกิดข้อผิดพลาดในการบันทึกข้อมูล: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep formData={formData} setFormData={setFormData} />;
      case 1:
        return <PricingStep formData={formData} setFormData={setFormData} calculateAmounts={calculateAmounts} />;
      case 2:
        return <PaymentStep formData={formData} setFormData={setFormData} calculateAmounts={calculateAmounts} />;
      case 3:
        return <ExpensesStep formData={formData} setFormData={setFormData} calculateAmounts={calculateAmounts} />;
      case 4:
        return <OtherExpensesStep formData={formData} setFormData={setFormData} />;
      case 5:
        return <DevelopersStep formData={formData} setFormData={setFormData} calculateAmounts={calculateAmounts} />;
      case 6:
        return <SummaryStep formData={formData} calculateAmounts={calculateAmounts} />;
      default:
        return <BasicInfoStep formData={formData} setFormData={setFormData} />;
    }
  };

  if (loading && isEdit) {
    return <PageLoading message="กำลังโหลดข้อมูลโปรเจค..." />;
  }

  return (
    <div className="min-h-screen bg-background">
      <ProjectFormHeader 
        isEdit={isEdit}
        loading={loading}
        onBack={handleBack}
        onSubmit={handleSubmit}
      />
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6 max-w-full">
          <ProjectFormSidebar 
            formData={formData}
            calculateAmounts={calculateAmounts}
          />
          
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {renderCurrentStep()}
              
              <StepNavigation
                currentStep={currentStep}
                totalSteps={steps.length}
                onPrevious={prevStep}
                onNext={nextStep}
                onSubmit={handleSubmit}
                loading={loading}
                isEdit={isEdit}
              />
              </div>
                </div>
              </div>
                </div>
                      </div>
  );
};

export default ProjectForm;
